import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Plus, Pencil, Trash2, FolderTree } from 'lucide-react';
import _ from 'lodash';
import PageContainer from 'ui/PageContainer';
import PageHeader from 'ui/PageHeader';
import TableContainer from 'ui/TableContainer';
import Table from 'ui/Table';
import {
    Button,
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from 'ui/shadcn';
import { staffClient } from 'clients/staff';
import { buildBoardColumns, type BoardRow } from './config';
import BoardFormDialog from './BoardFormDialog';

const PAGE_SIZE = 10;

const BoardsPage: React.FC = () => {
    const navigate = useNavigate();

    const [rows, setRows] = useState<BoardRow[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);

    const [formOpen, setFormOpen] = useState(false);
    const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
    const [editing, setEditing] = useState<BoardRow | null>(null);

    const [deleteTarget, setDeleteTarget] = useState<BoardRow | null>(null);
    const [deleting, setDeleting] = useState(false);

    const fetchPage = useCallback(async (p: number) => {
        setLoading(true);
        try {
            const params = { offset: (p - 1) * PAGE_SIZE, limit: PAGE_SIZE };
            const res = await staffClient.get('/board', { params });
            setRows(res.data.data || []);
            setTotal(res.data.meta?.total || 0);
            setPage(p);
        } catch (e) {
            console.error('[fetchBoards] error', e);
            toast.error('게시판 목록을 불러오지 못했습니다.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchPage(1);
    }, [fetchPage]);

    const openCreate = () => {
        setFormMode('create');
        setEditing(null);
        setFormOpen(true);
    };

    const openEdit = (row: BoardRow) => {
        setFormMode('edit');
        setEditing(row);
        setFormOpen(true);
    };

    const handleSubmit = async (form: {
        name: string;
        description: string;
        visible: boolean;
    }) => {
        try {
            if (formMode === 'create') {
                await staffClient.post('/board', form);
                toast.success('게시판이 생성되었습니다.');
            } else if (editing) {
                await staffClient.patch(`/board/${editing.id}`, form);
                toast.success('게시판이 수정되었습니다.');
            }
            await fetchPage(page);
        } catch (e: any) {
            const msg = e?.response?.data?.message;
            toast.error(msg || (formMode === 'create' ? '생성 실패' : '수정 실패'));
            throw e;
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await staffClient.delete(`/board/${deleteTarget.id}`);
            toast.success('게시판이 삭제되었습니다.');
            setDeleteTarget(null);
            await fetchPage(page);
        } catch (e: any) {
            const msg = e?.response?.data?.message;
            if (!_.isNil(msg) && msg.includes('FOREIGN')) {
                toast.info('연결된 현장이 존재합니다.');
            } else {
                toast.error(msg || '게시판 삭제에 실패했습니다.');
            }
        } finally {
            setDeleting(false);
        }
    };

    const columns = buildBoardColumns((row) =>
        navigate(`/admin/boards/${row.id}/categories`)
    );

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    return (
        <PageContainer>
            <PageHeader
                title="게시판 관리"
                subtitle="인트라넷에 노출되는 게시판을 생성·수정합니다."
                breadcrumb={[{ label: '관리자', path: '/admin' }, { label: '게시판' }]}
                actions={
                    <Button onClick={openCreate}>
                        <Plus />
                        게시판 추가
                    </Button>
                }
            />

            <TableContainer title="게시판 목록" resultCount={total}>
                <Table<BoardRow>
                    columns={columns}
                    data={rows}
                    loading={loading}
                    emptyMessage="등록된 게시판이 없습니다."
                    actions={(row) => (
                        <>
                            <Button
                                variant="ghost"
                                size="icon-sm"
                                onClick={() =>
                                    navigate(`/admin/boards/${row.id}/categories`)
                                }
                                title="카테고리 관리"
                            >
                                <FolderTree />
                            </Button>
                            <Button
                                variant="ghost"
                                size="icon-sm"
                                onClick={() => openEdit(row)}
                                title="수정"
                            >
                                <Pencil />
                            </Button>
                            <Button
                                variant="ghost"
                                size="icon-sm"
                                onClick={() => setDeleteTarget(row)}
                                title="삭제"
                                className="text-destructive hover:bg-red-50"
                            >
                                <Trash2 />
                            </Button>
                        </>
                    )}
                    actionsColumnWidth="140px"
                />

                {totalPages > 1 && (
                    <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-border bg-neutral-50">
                        <span className="text-xs text-muted-foreground tabular-nums">
                            <span className="font-medium text-foreground">{page}</span> /{' '}
                            <span className="font-medium text-foreground">{totalPages}</span> 페이지 · 총{' '}
                            <span className="font-medium text-foreground">{total}</span>건
                        </span>
                        <div className="flex items-center gap-1">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => fetchPage(1)}
                                disabled={page === 1 || loading}
                            >
                                처음
                            </Button>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => fetchPage(page - 1)}
                                disabled={page === 1 || loading}
                            >
                                이전
                            </Button>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => fetchPage(page + 1)}
                                disabled={page === totalPages || loading}
                            >
                                다음
                            </Button>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => fetchPage(totalPages)}
                                disabled={page === totalPages || loading}
                            >
                                마지막
                            </Button>
                        </div>
                    </div>
                )}
            </TableContainer>

            <BoardFormDialog
                open={formOpen}
                mode={formMode}
                initial={editing || undefined}
                onClose={() => setFormOpen(false)}
                onSubmit={handleSubmit}
            />

            <AlertDialog
                open={Boolean(deleteTarget)}
                onOpenChange={(open) => !open && !deleting && setDeleteTarget(null)}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>게시판 삭제</AlertDialogTitle>
                        <AlertDialogDescription>
                            <span className="font-medium text-foreground">
                                {deleteTarget?.name}
                            </span>{' '}
                            게시판을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={deleting}>취소</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => {
                                e.preventDefault();
                                handleDelete();
                            }}
                            disabled={deleting}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            {deleting ? '삭제 중...' : '삭제'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </PageContainer>
    );
};

export default BoardsPage;
